import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Check,
  ChevronRight,
  ArrowLeft,
  Zap,
  Users,
  Building2
} from 'lucide-react';

const PricingPage = () => {
  const { user } = useAuth();

  const plans = [
    {
      name: 'Athlete',
      price: '$3',
      period: '/month',
      description: 'Perfect for individual athletes',
      features: [
        'Full training tracking',
        'AI workout recommendations',
        'Nutrition logging & AI suggestions',
        'Recovery monitoring',
        'Schedule management',
        '1 supporter link',
        'Progress badges & streaks'
      ],
      cta: 'Start Free Trial',
      popular: false,
      icon: Zap,
      color: 'aura-primary'
    },
    {
      name: 'Family',
      price: '$3 + $1',
      period: '/athlete',
      description: 'For families with multiple athletes',
      features: [
        'Everything in Athlete plan',
        'Up to 4 athlete accounts',
        'Shared family calendar',
        'Parent dashboard',
        'Sibling meal planning',
        'Combined grocery lists',
        '$6/month max'
      ],
      cta: 'Start Free Trial',
      popular: true,
      icon: Users,
      color: 'aura-success'
    },
    {
      name: 'Organization',
      price: 'Choose',
      period: 'your path',
      description: 'For schools, clubs & academies',
      features: [
        'Path A: $49/mo + $2/athlete (Org pays)',
        'Path B: FREE for org (Athletes pay $3)',
        'Team roster management',
        'Team-wide dashboards',
        'Recovery alerts',
        'Attendance tracking',
        'No budget? Choose Path B!'
      ],
      cta: 'Register Organization',
      ctaLink: '/organization/register',
      popular: false,
      icon: Building2,
      color: 'aura-secondary'
    }
  ];

  return (
    <div className="min-h-screen bg-aura-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-aura-bg/95 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-aura-text-muted hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Back</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-aura-primary rounded-sm flex items-center justify-center">
              <span className="font-heading font-black text-white text-lg">A</span>
            </div>
            <span className="font-heading font-bold text-lg text-white tracking-tight">AURA</span>
          </Link>
          <div className="w-20" /> {/* Spacer for centering */}
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-12 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-aura-text-muted max-w-2xl mx-auto">
            Start with a 14-day free trial. No credit card required.
            Cancel anytime.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <Card 
              key={plan.name}
              className={`bg-aura-surface border-white/10 relative overflow-hidden ${
                plan.popular ? 'ring-2 ring-aura-success' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0 bg-aura-success text-white text-xs font-heading uppercase tracking-wider px-3 py-1">
                  Most Popular
                </div>
              )}
              <CardContent className="p-6">
                <div className={`w-12 h-12 rounded-lg bg-${plan.color}/20 flex items-center justify-center mb-4`}>
                  <plan.icon className={`w-6 h-6 text-${plan.color}`} />
                </div>
                
                <h3 className="font-heading font-bold text-xl text-white uppercase tracking-wide mb-1">
                  {plan.name}
                </h3>
                <p className="text-sm text-aura-text-muted mb-4">{plan.description}</p>
                
                <div className="flex items-baseline gap-1 mb-6">
                  <span className={`font-heading font-bold text-4xl text-${plan.color}`}>{plan.price}</span>
                  <span className="text-aura-text-muted">{plan.period}</span>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-aura-text-muted">
                      <Check className={`w-4 h-4 text-${plan.color} flex-shrink-0 mt-0.5`} />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Link to={plan.ctaLink || "/register"}>
                  <Button 
                    className={`w-full font-heading uppercase tracking-wider h-12 ${
                      plan.popular 
                        ? 'bg-aura-success hover:bg-aura-success/90 text-white' 
                        : `bg-${plan.color} hover:bg-${plan.color}/90 text-white`
                    }`}
                  >
                    {plan.cta}
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight text-center mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            <div className="aura-card p-5">
              <h3 className="font-heading font-bold text-white mb-2">How does the free trial work?</h3>
              <p className="text-aura-text-muted text-sm">
                You get 14 days of full access to all features. No credit card required. 
                After the trial, you can subscribe or continue with view-only access to your data.
              </p>
            </div>
            
            <div className="aura-card p-5">
              <h3 className="font-heading font-bold text-white mb-2">How does family pricing work?</h3>
              <p className="text-aura-text-muted text-sm">
                First athlete is $3/month. Each additional family member is just $1/month. 
                Maximum family cost is $6/month for up to 4 athletes. One parent account included.
              </p>
            </div>
            
            <div className="aura-card p-5">
              <h3 className="font-heading font-bold text-white mb-2">Can I switch between plans?</h3>
              <p className="text-aura-text-muted text-sm">
                Yes! You can upgrade or downgrade at any time. Changes take effect on your next billing cycle.
              </p>
            </div>
            
            <div className="aura-card p-5">
              <h3 className="font-heading font-bold text-white mb-2">What happens to my data if I cancel?</h3>
              <p className="text-aura-text-muted text-sm">
                Your data is never deleted. You'll have view-only access to all your history. 
                Resubscribe anytime to regain full access.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <Link to="/register">
            <Button className="bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider text-lg h-14 px-10">
              Start Your Free Trial
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <p className="text-aura-text-subtle text-sm mt-4">
            14 days free. No credit card required.
          </p>
        </div>
      </main>
    </div>
  );
};

export default PricingPage;
